import { useReward } from "api/rewards";
import { createHeadItems, OLTable, Row } from "components/Table/OLTable";
import { calculateCount } from "components/Table/tableHelpers";
import {
  ModalMode,
  RewardRedemptionModals,
} from "components/Tooltips/RewardRedemptionModals";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { REDEEMED_REWARDS_LIST_ID } from "state/tables/types";
import { ListingActions, ListResponse } from "types/listing";
import { Coupon, RedeemedReward } from "types/rewards";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";

function CreateItems(items: RedeemedReward[]): Row[] {
  const { message } = useMessage();
  return items.map((item: RedeemedReward) => ({
    id: item.issuedRewardId,
    cells: [
      { content: item.name, property: "name" },
      {
        content: OLDate(item.issuedCoupon?.activeTo),
        property: "issuedCoupon:activeTo",
      },
      { content: OLDate(item.redemptionDate), property: "redemptionDate" },
      { content: item.issuedRewardId, property: "issuedRewardId" },
      { content: message({ id: item.status }), property: "status" },
    ],
    metadata: {
      issuedCoupon: item.issuedCoupon,
      issuedRewardId: item.issuedRewardId,
      rewardId: item.rewardId,
      rewardType: item.rewardType,
    },
  }));
}

export function RedeemedRewards({ memberID }: { memberID: string }) {
  const { message } = useMessage();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [selectedModal, setModal] = useState<{
    issuedCoupon?: Coupon;
    issuedReward?: string;
    memberId?: string;
    mode: number;
  }>();
  const { useRewardsFulfillmentList } = useReward();
  const { listing, updateListing } = useListing(REDEEMED_REWARDS_LIST_ID);
  const { setColumnOrdering } = useTablesStateHelpers();
  const { data: membersRedeemedRewards } = useRewardsFulfillmentList({
    filters: memberID
      ? [
          {
            condition: "eq",
            query: "customerId",
            value: memberID,
          },
        ]
      : [],
    order: listing.order || "desc",
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property || "redemptionDate",
  }) as { data: ListResponse };

  const headItems = createHeadItems([
    {
      headerText: message({ id: "rewardName" }),
      property: "name",
      isSortable: true,
    },
    {
      headerText: message({ id: "activeTo" }),
      property: "issuedCoupon:activeTo",
      isSortable: true,
    },
    {
      headerText: message({ id: "redemptionDate" }),
      property: "redemptionDate",
      isSortable: true,
    },
    {
      headerText: message({ id: "redeemedRewardId" }),
      property: "issuedRewardId",
      isSortable: true,
    },
    {
      headerText: message({ id: "status" }),
      property: "status",
      isSortable: true,
    },
  ]);

  const items = membersRedeemedRewards?.items
    ? CreateItems(membersRedeemedRewards.items)
    : [];

  return (
    <>
      <OLTable
        setColumnOrdering={setColumnOrdering}
        tableName={REDEEMED_REWARDS_LIST_ID}
        data-test-id="single-member-redeemed-rewards"
        actions={{
          body: [
            {
              label: message({ id: "changeStatus" }),
              action: (_, item?: Row) => {
                setOpen(true);
                setModal({
                  issuedReward: item?.metadata.issuedRewardId as string,
                  mode: ModalMode.ChangeStatus,
                });
              },
            },
            {
              label: message({ id: "seeDetails" }),
              action: (_, item?: Row) => {
                navigate(
                  links.rewardsFulfillment(item?.metadata?.issuedRewardId),
                  {
                    state: { prevPath: links.singleMemberRewards(memberID) },
                  },
                );
              },
            },
            {
              label: message({ id: "seeReward" }),
              action: (_, item?: Row) => {
                navigate(links.reward(item?.metadata?.rewardId), {
                  state: { prevPath: links.singleMemberRewards(memberID) },
                });
              },
            },
            {
              label: message({ id: "showCoupon" }),
              action: (_, item?: Row) => {
                setOpen(true);
                setModal({
                  issuedCoupon: item?.metadata?.issuedCoupon,
                  mode: ModalMode.Coupon,
                });
              },
              hideCondition: (item: Row) => !item.metadata.issuedCoupon,
            },
            {
              label: message({ id: "markAsUnused" }),
              action: (_, item?: Row) => {
                setOpen(true);
                setModal({
                  issuedCoupon: item?.metadata?.issuedCoupon,
                  memberId: memberID,
                  mode: ModalMode.ReissueAssignedCoupon,
                });
              },
              hideCondition: (item: Row) =>
                !item.metadata.issuedCoupon ||
                !item.metadata.issuedCoupon.usedAt,
            },
            {
              label: message({ id: "markAsUsed" }),
              action: (_, item?: Row) => {
                setOpen(true);
                setModal({
                  issuedCoupon: item?.metadata?.issuedCoupon,
                  memberId: memberID,
                  mode: ModalMode.RedeemAssignedCoupon,
                });
              },
              hideCondition: (item: Row) =>
                !item.metadata.issuedCoupon ||
                !!item.metadata.issuedCoupon.usedAt,
            },
          ],
        }}
        columns={membersRedeemedRewards && headItems}
        onSort={(prop) =>
          updateListing({
            payload: prop,
            type: ListingActions.SET_SORT,
          })
        }
        rows={membersRedeemedRewards && items}
        sort={{
          order: listing.order || "desc",
          property: listing.property || "redemptionDate",
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
        }}
        pagination={{
          count: calculateCount(
            membersRedeemedRewards?.total.filtered,
            membersRedeemedRewards?.total.all,
          ),
          page: listing.page,
          onChangePage: (page) => {
            updateListing({
              payload: page,
              type: ListingActions.CHANGE_PAGE,
            });
          },
          onChangeRowsPerPage: (rows) => {
            updateListing({
              payload: rows,
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
            });
          },
          rowsPerPage: listing.rowsPerPage,
        }}
        onRowClick={(id: string) => {
          navigate(links.rewardsFulfillment(id as string), {
            state: { prevPath: links.singleMemberRewards(memberID) },
          });
        }}
      />
      <RewardRedemptionModals
        close={() => setOpen(false)}
        open={open}
        selectedModal={selectedModal}
      />
    </>
  );
}
